import React, { useCallback, useState, createContext, useContext } from 'react';
type Lang = 'en' | 'es';
interface LanguageContextType {
  lang: Lang;
  toggleLang: () => void;
  t: (key: string) => string;
}
const translations: Record<string, Record<Lang, string>> = {
  // Navbar
  'nav.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'nav.matches': {
    en: 'Matches',
    es: 'Partidos'
  },
  'nav.deals': {
    en: 'Deals',
    es: 'Ofertas'
  },
  'nav.liveScores': {
    en: 'Live Scores',
    es: 'Resultados en Vivo'
  },
  'nav.orderYours': {
    en: 'Order Yours',
    es: 'Pide la Tuya'
  },
  // Hero
  'hero.badge': {
    en: "You're now part of Canada 2026",
    es: 'Ahora eres parte de Canadá 2026'
  },
  'hero.title1': {
    en: "Don't Just Support Canada,",
    es: 'No Solo Apoyes a Canadá,'
  },
  'hero.title2': {
    en: 'Live It',
    es: 'Vívelo'
  },
  'hero.subtitle': {
    en: 'Updated in real live time. Access exclusive content, match updates, and travel deals directly from your digital hub.',
    es: 'Actualizado en tiempo real. Accede a contenido exclusivo, actualizaciones de partidos y ofertas de viaje directamente desde tu hub digital.'
  },
  'hero.cta1': {
    en: 'Watch Matches',
    es: 'Ver Partidos'
  },
  'hero.cta2': {
    en: 'Explore Team',
    es: 'Explorar Equipo'
  },
  'hero.scroll': {
    en: 'Scroll to discover',
    es: 'Desliza para descubrir'
  },
  // Team Overview
  'team.label': {
    en: 'Our Legacy',
    es: 'Nuestro Legado'
  },
  'team.title1': {
    en: 'A Nation Forged',
    es: 'Una Nación Forjada'
  },
  'team.title2': {
    en: 'In Red & White',
    es: 'En Rojo y Blanco'
  },
  'team.desc': {
    en: 'The Canadian national team represents more than just football; it embodies the passion, resilience, and diversity of a nation. From the historic triumph in the 2000 Gold Cup to the incredible Copa America 2024 run, Les Rouges continue to write their name in the stars.',
    es: 'La selección canadiense representa más que solo fútbol; encarna la pasión, resiliencia y diversidad de una nación. Desde el histórico triunfo en la Copa Oro 2000 hasta la increíble campaña en la Copa América 2024, Les Rouges continúan escribiendo su nombre en las estrellas.'
  },
  'team.since': {
    en: 'Since 1924',
    es: 'Desde 1924'
  },
  'team.history': {
    en: 'Explore Our Full History →',
    es: 'Explora Nuestra Historia Completa →'
  },
  'team.achievement1': {
    en: 'Gold Cup 2000',
    es: 'Copa Oro 2000'
  },
  'team.achievement1Desc': {
    en: 'Champions of CONCACAF',
    es: 'Campeones de CONCACAF'
  },
  'team.achievement2': {
    en: 'Copa America 2024',
    es: 'Copa América 2024'
  },
  'team.achievement2Desc': {
    en: 'Historic Semifinalists',
    es: 'Semifinalistas Históricos'
  },
  'team.achievement3': {
    en: 'World Cup 2022',
    es: 'Mundial 2022'
  },
  'team.achievement3Desc': {
    en: 'Return to the Global Stage',
    es: 'Regreso al Escenario Global'
  },
  'team.achievement4': {
    en: 'Co-Hosts 2026',
    es: 'Coanfitriones 2026'
  },
  'team.achievement4Desc': {
    en: 'Bringing the World Home',
    es: 'Trayendo el Mundo a Casa'
  },
  'team.videoLabel': {
    en: 'Canadian Spirit',
    es: 'Espíritu Canadiense'
  },
  'team.videoTitle1': {
    en: 'One Nation, One',
    es: 'Una Nación, Un'
  },
  'team.videoTitle2': {
    en: 'Heartbeat',
    es: 'Latido'
  },
  // Key Players
  'players.label': {
    en: 'The Squad',
    es: 'La Plantilla'
  },
  'players.title1': {
    en: 'The Stars of',
    es: 'Las Estrellas de'
  },
  'players.title2': {
    en: 'Canada',
    es: 'Canadá'
  },
  'players.viewSquad': {
    en: 'View Full Squad on ESPN.com',
    es: 'Ver Plantilla Completa en ESPN.com'
  },
  'players.forward': {
    en: 'Forward',
    es: 'Delantero'
  },
  'players.midfielder': {
    en: 'Midfielder',
    es: 'Mediocampista'
  },
  'players.defender': {
    en: 'Defender',
    es: 'Defensa'
  },
  // Match Center
  'matches.label': {
    en: 'World Cup 2026',
    es: 'Mundial 2026'
  },
  'matches.title1': {
    en: 'Match',
    es: 'Centro de'
  },
  'matches.title2': {
    en: 'Center',
    es: 'Partidos'
  },
  'matches.desc': {
    en: 'Follow Les Rouges in Group A as Co-Hosts. Get live updates, official lineups, and real-time scores from the biggest stage in football.',
    es: 'Sigue a Les Rouges en el Grupo A como Coanfitriones. Obtén actualizaciones en vivo, alineaciones oficiales y resultados en tiempo real del mayor escenario del fútbol.'
  },
  'matches.standings': {
    en: 'Group Standings',
    es: 'Clasificación del Grupo'
  },
  'matches.live': {
    en: 'Live',
    es: 'En Vivo'
  },
  'matches.autoRefresh': {
    en: 'Auto-refreshes every 5 min',
    es: 'Se actualiza automáticamente cada 5 min'
  },
  'matches.refreshNow': {
    en: 'Refresh Now',
    es: 'Actualizar Ahora'
  },
  'matches.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'matches.viewStandings': {
    en: 'View Live Standings on FlashScore',
    es: 'Ver Clasificación en Vivo en FlashScore'
  },
  'matches.nextMatch': {
    en: 'Next Match',
    es: 'Próximo Partido'
  },
  'matches.espn': {
    en: 'ESPN Schedule',
    es: 'Calendario ESPN'
  },
  'matches.sofascore': {
    en: 'SofaScore',
    es: 'SofaScore'
  },
  'matches.flashscore': {
    en: 'FlashScore Live',
    es: 'FlashScore en Vivo'
  },
  'matches.foxsports': {
    en: 'Fox Sports',
    es: 'Fox Sports'
  },
  // Stats
  'stats.label': {
    en: 'Canada By The Numbers',
    es: 'Canadá en Números'
  },
  'stats.worldCup': {
    en: 'World Cup Appearances',
    es: 'Participaciones en Mundiales'
  },
  'stats.titles': {
    en: 'Major Titles',
    es: 'Títulos Principales'
  },
  'stats.goals': {
    en: 'International Goals',
    es: 'Goles Internacionales'
  },
  'stats.capped': {
    en: 'Most Capped (Hutchinson)',
    es: 'Más Partidos (Hutchinson)'
  },
  // Historic Moments
  'history.label': {
    en: 'The Archives',
    es: 'Los Archivos'
  },
  'history.title1': {
    en: 'Moments That',
    es: 'Momentos Que'
  },
  'history.title2': {
    en: 'Made History',
    es: 'Hicieron Historia'
  },
  'history.v1': {
    en: 'WC 2022 Qualification',
    es: 'Clasificación al Mundial 2022'
  },
  'history.v2': {
    en: 'Davies Bicycle Kick vs Panama',
    es: 'Chilena de Davies vs Panamá'
  },
  'history.v3': {
    en: 'Canada at Qatar 2022',
    es: 'Canadá en Qatar 2022'
  },
  'history.v4': {
    en: '1986 World Cup Debut',
    es: 'Debut en el Mundial 1986'
  },
  'history.v5': {
    en: 'Gold Cup 2000 Champions',
    es: 'Campeones Copa Oro 2000'
  },
  'history.v6': {
    en: 'Copa America 2024 Semifinal Run',
    es: 'Semifinales Copa América 2024'
  },
  'history.v7': {
    en: 'Jonathan David Goals Compilation',
    es: 'Goles de Jonathan David'
  },
  'history.v8': {
    en: 'Atiba Hutchinson Tribute',
    es: 'Tributo a Atiba Hutchinson'
  },
  'history.v9': {
    en: '2026 World Cup Host Announcement',
    es: 'Anuncio Sede Mundial 2026'
  },
  'history.v10': {
    en: 'Canada vs Mexico Rivalry',
    es: 'Rivalidad Canadá vs México'
  },
  // Fan Experience
  'fan.label': {
    en: 'More Than A Game',
    es: 'Más Que Un Juego'
  },
  'fan.title1': {
    en: 'Feel the Passion of',
    es: 'Siente la Pasión de'
  },
  'fan.title2': {
    en: 'Canada',
    es: 'Canadá'
  },
  'fan.desc': {
    en: 'Join millions of voices singing as one. Wear the colors, feel the history, and be part of the journey as we host the world.',
    es: 'Únete a millones de voces cantando como una sola. Viste los colores, siente la historia y sé parte del viaje mientras somos anfitriones del mundo.'
  },
  // Tickets / Travel Deals
  'deals.label': {
    en: 'Travel to the World Cup',
    es: 'Viaja al Mundial'
  },
  'deals.title1': {
    en: 'Exclusive',
    es: 'Ofertas Exclusivas'
  },
  'deals.title2': {
    en: 'Fan Deals',
    es: 'para Fans'
  },
  'deals.desc': {
    en: 'Exclusive discounts for Canada fans on tickets, hotels, flights & transport to support Les Rouges across the host cities.',
    es: 'Descuentos exclusivos para fans de Canadá en boletos, hoteles, vuelos y transporte para apoyar a Les Rouges en las ciudades sede.'
  },
  'deals.unlockVip': {
    en: 'Unlock VIP Discounts',
    es: 'Desbloquea Descuentos VIP'
  },
  'deals.applyCode': {
    en: 'Apply code at checkout on partner sites',
    es: 'Aplica el código al pagar en sitios asociados'
  },
  'deals.tickets': {
    en: 'Match Tickets',
    es: 'Boletos de Partido'
  },
  'deals.ticketsDesc': {
    en: 'Up to 15% off group stage packages',
    es: 'Hasta 15% de descuento en paquetes de fase de grupos'
  },
  'deals.findTickets': {
    en: 'Find Tickets',
    es: 'Buscar Boletos'
  },
  'deals.hotels': {
    en: 'Hotels & Accommodation',
    es: 'Hoteles y Alojamiento'
  },
  'deals.hotelsDesc': {
    en: 'Partner hotel deals near match venues',
    es: 'Ofertas de hoteles asociados cerca de los estadios'
  },
  'deals.bookHotels': {
    en: 'Book Hotels',
    es: 'Reservar Hoteles'
  },
  'deals.flights': {
    en: 'Flights',
    es: 'Vuelos'
  },
  'deals.flightsDesc': {
    en: 'Special domestic fares across Canada',
    es: 'Tarifas nacionales especiales en todo Canadá'
  },
  'deals.searchFlights': {
    en: 'Search Flights',
    es: 'Buscar Vuelos'
  },
  'deals.transport': {
    en: 'Local Transport',
    es: 'Transporte Local'
  },
  'deals.transportDesc': {
    en: 'Shuttle services & car rentals at match cities',
    es: 'Servicios de transporte y alquiler de autos en ciudades sede'
  },
  'deals.planTransport': {
    en: 'Plan Transport',
    es: 'Planear Transporte'
  },
  // Fan HQ / Gallery
  'fanHq.label': {
    en: 'Everything You Need for Canada 2026',
    es: 'Todo lo que Necesitas para Canadá 2026'
  },
  'fanHq.title1': {
    en: 'Fan',
    es: 'Sede del'
  },
  'fanHq.title2': {
    en: 'HQ',
    es: 'Fan'
  },
  'fanHq.countdown': {
    en: "Countdown to Canada's First Match",
    es: 'Cuenta Regresiva para el Primer Partido de Canadá'
  },
  'fanHq.days': {
    en: 'Days',
    es: 'Días'
  },
  'fanHq.hrs': {
    en: 'Hrs',
    es: 'Hrs'
  },
  'fanHq.min': {
    en: 'Min',
    es: 'Min'
  },
  'fanHq.sec': {
    en: 'Sec',
    es: 'Seg'
  },
  'fanHq.capacity': {
    en: 'Capacity',
    es: 'Capacidad'
  },
  'fanHq.nationalAnthem': {
    en: 'National Anthem',
    es: 'Himno Nacional'
  },
  'fanHq.watchYoutube': {
    en: 'Watch on YouTube',
    es: 'Ver en YouTube'
  },
  'fanHq.quickLinks': {
    en: 'Quick',
    es: 'Enlaces'
  },
  'fanHq.quickLinksHighlight': {
    en: 'Links',
    es: 'Rápidos'
  },
  'fanHq.whereToWatch': {
    en: 'Where to Watch',
    es: 'Dónde Ver'
  },
  'fanHq.whereToWatchDesc': {
    en: 'Streaming & TV channels',
    es: 'Canales de TV y streaming'
  },
  'fanHq.fanZones': {
    en: 'Fan Info Channel',
    es: 'Canal de Info para Fans'
  },
  'fanHq.fanZonesDesc': {
    en: 'Official FIFA fan information',
    es: 'Información oficial de FIFA para fans'
  },
  'fanHq.fifaRanking': {
    en: 'FIFA Ranking',
    es: 'Ranking FIFA'
  },
  'fanHq.fifaRankingDesc': {
    en: 'Official world rankings',
    es: 'Rankings mundiales oficiales'
  },
  'fanHq.fullSchedule': {
    en: 'Full WC Schedule',
    es: 'Calendario Completo del Mundial'
  },
  'fanHq.fullScheduleDesc': {
    en: 'All 104 matches',
    es: 'Todos los 104 partidos'
  },
  // Footer
  'footer.desc': {
    en: 'An official Qtap by STDI digital product. Your smart NFC T-shirt hub for Canada 2026 — real-time match data, exclusive content, and travel deals at your fingertips.',
    es: 'Un producto digital oficial de Qtap by STDI. Tu hub de camiseta inteligente NFC para Canadá 2026: datos de partidos en tiempo real, contenido exclusivo y ofertas de viaje al alcance de tu mano.'
  },
  'footer.resources': {
    en: 'Resources',
    es: 'Recursos'
  },
  'footer.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'footer.firstTeam': {
    en: 'First Team',
    es: 'Primer Equipo'
  },
  'footer.matches': {
    en: 'Matches',
    es: 'Partidos'
  },
  'footer.standings': {
    en: 'Standings',
    es: 'Clasificación'
  },
  'footer.travelDeals': {
    en: 'Travel Deals',
    es: 'Ofertas de Viaje'
  },
  'footer.support': {
    en: 'Support',
    es: 'Soporte'
  },
  'footer.contactUs': {
    en: 'Contact Us',
    es: 'Contáctanos'
  },
  'footer.faq': {
    en: 'FAQ',
    es: 'Preguntas Frecuentes'
  },
  'footer.privacy': {
    en: 'Privacy Policy',
    es: 'Política de Privacidad'
  },
  'footer.terms': {
    en: 'Terms of Service',
    es: 'Términos de Servicio'
  },
  'footer.cookies': {
    en: 'Cookie Policy',
    es: 'Política de Cookies'
  },
  'footer.copyright': {
    en: 'Qtap by STDI. All rights reserved.',
    es: 'Qtap by STDI. Todos los derechos reservados.'
  }
};
const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  toggleLang: () => {},
  t: (key: string) => key
});
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLang] = useState<Lang>('en');
  const toggleLang = useCallback(() => {
    setLang(prev => prev === 'en' ? 'es' : 'en');
  }, []);
  const t = useCallback((key: string) => {
    return translations[key]?.[lang] || key;
  }, [lang]);
  return <LanguageContext.Provider value={{
    lang,
    toggleLang,
    t
  }} data-id="element-211">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  return useContext(LanguageContext);
}